import { useEffect, useMemo, useRef } from 'react';
import { useFormikContext } from 'formik';
import { Box, Typography } from '@mui/material';

import ButtonUI from './ButtonUI';
import DialogUI from './DialogUI';

export const hasNestedErrors = (value) => {
  if (!value) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some((item) => hasNestedErrors(item));
  if (typeof value === 'object') return Object.values(value).some((item) => hasNestedErrors(item));
  return false;
};

export const buildTouchedFromErrors = (value) => {
  if (!value) return undefined;
  if (typeof value === 'string') return true;

  if (Array.isArray(value)) {
    const touchedArray = value.map((item) => buildTouchedFromErrors(item));
    return touchedArray.some((item) => item !== undefined) ? touchedArray : undefined;
  }

  if (typeof value === 'object') {
    const touchedObject = Object.entries(value).reduce((acc, [key, item]) => {
      const touchedValue = buildTouchedFromErrors(item);
      if (touchedValue !== undefined) {
        acc[key] = touchedValue;
      }
      return acc;
    }, {});

    return Object.keys(touchedObject).length > 0 ? touchedObject : undefined;
  }

  return undefined;
};

const flattenValidationErrors = (value, path = [], items = []) => {
  if (!value) return items;

  if (typeof value === 'string') {
    items.push({ path, message: value });
    return items;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      flattenValidationErrors(item, [...path, index], items);
    });
    return items;
  }

  if (typeof value === 'object') {
    Object.entries(value).forEach(([key, item]) => {
      flattenValidationErrors(item, [...path, key], items);
    });
  }

  return items;
};

const getFieldLabel = (field, fieldLabels) => fieldLabels?.[field] ?? field;

const findSectionRule = (root, sectionRules) => {
  const safeRoot = String(root ?? '');

  return (
    sectionRules.find((rule) => rule.roots?.has?.(safeRoot)) ||
    sectionRules.find((rule) => rule.fields?.has?.(safeRoot)) ||
    sectionRules.find((rule) => rule.matcher?.(safeRoot)) || {
      section: 'Fejléc',
    }
  );
};

const formatValidationLocation = (path, sectionRules, fieldLabels) => {
  const [root, index, nestedRoot, nestedIndex, field] = path;
  const sectionRule = findSectionRule(root, sectionRules);

  if (sectionRule.roots?.has?.(String(root)) && typeof index === 'number') {
    const itemLabel = sectionRule.itemLabel ?? 'tétel';

    if (typeof nestedIndex === 'number') {
      const nestedItemLabel = sectionRule.nestedItemLabels?.[nestedRoot] ?? 'elem';
      return {
        section: sectionRule.section,
        label: `${index + 1}. ${itemLabel} / ${nestedIndex + 1}. ${nestedItemLabel} / ${getFieldLabel(field, fieldLabels)}`,
      };
    }

    return {
      section: sectionRule.section,
      label: `${index + 1}. ${itemLabel} / ${getFieldLabel(nestedRoot, fieldLabels)}`,
    };
  }

  return {
    section: sectionRule.section,
    label: getFieldLabel(root, fieldLabels),
  };
};

const buildValidationSummary = (errors, { sectionRules = [], fieldLabels = {} } = {}) => {
  const uniqueItems = new Map();

  flattenValidationErrors(errors).forEach(({ path, message }) => {
    const { section, label } = formatValidationLocation(path, sectionRules, fieldLabels);
    const key = `${section}|${label}|${message}`;

    if (!uniqueItems.has(key)) {
      uniqueItems.set(key, {
        id: key,
        section,
        label,
        message,
      });
    }
  });

  return Array.from(uniqueItems.values());
};

export const ValidationSummaryWatcher = ({ onValidationFailed }) => {
  const lastHandledSubmitCount = useRef(0);
  const { submitCount, errors, isSubmitting, isValidating } = useFormikContext();

  useEffect(() => {
    if (submitCount === 0) {
      lastHandledSubmitCount.current = 0;
      return;
    }

    if (isSubmitting || isValidating || submitCount <= lastHandledSubmitCount.current) return;

    lastHandledSubmitCount.current = submitCount;

    if (hasNestedErrors(errors)) {
      onValidationFailed(errors);
    }
  }, [submitCount, errors, isSubmitting, isValidating, onValidationFailed]);

  return null;
};

const ValidationSummaryDialog = ({
  open,
  onClose,
  errors,
  sectionRules,
  fieldLabels,
  title = 'Validációs hibák',
  description,
  closeButtonText = 'Rendben',
}) => {
  const items = useMemo(
    () => buildValidationSummary(errors, { sectionRules, fieldLabels }),
    [errors, sectionRules, fieldLabels]
  );

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      headerContent={`${title} (${items.length})`}
      fullWidth={true}
      maxWidth="md"
      actionsContent={
        <ButtonUI type="button" text={closeButtonText} style={{ backgroundColor: '#1976d2' }} onClick={onClose} />
      }
    >
      <Box sx={{ pt: 1 }}>
        <Typography sx={{ mb: 2, color: 'text.primary' }}>{description}</Typography>

        <Box sx={{ maxHeight: 420, overflow: 'auto', pr: 1 }}>
          <Box
            sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: 'minmax(120px, 0.7fr) minmax(220px, 1.1fr) minmax(180px, 1fr)',
              gap: 1.5,
              px: 1.5,
              pb: 1,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
              Szekció
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
              Mező
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
              Hiba
            </Typography>
          </Box>

          {items.map((item) => (
            <Box
              key={item.id}
              sx={{
                mb: 1,
                px: 1.5,
                py: 1.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'error.light',
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
                display: 'grid',
                gap: 1,
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'minmax(120px, 0.7fr) minmax(220px, 1.1fr) minmax(180px, 1fr)',
                },
                alignItems: 'start',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                {item.section}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ color: 'error.main' }}>
                {item.message}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </DialogUI>
  );
};

export default ValidationSummaryDialog;
